import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import RefreshIcon from "@mui/icons-material/Refresh";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { ManagedUserResponse, UsersService } from "../../../api";

const MySwal = withReactContent(Swal);

type PasswordMode = "manual" | "generate";

type ResetPasswordDialogProps = {
  open: boolean;
  user: ManagedUserResponse | null;
  onClose: () => void;
};

function generatePassword(length = 12): string {
  const lower = "abcdefghijklmnopqrstuvwxyz";
  const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const digits = "0123456789";
  const all = lower + upper + digits;

  const pick = (charset: string) =>
    charset[Math.floor(Math.random() * charset.length)];

  const chars = [pick(lower), pick(upper), pick(digits)];
  for (let i = chars.length; i < length; i++) {
    chars.push(pick(all));
  }

  for (let i = chars.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }

  return chars.join("");
}

export default function ResetPasswordDialog({
  open,
  user,
  onClose,
}: ResetPasswordDialogProps) {
  const [mode, setMode] = useState<PasswordMode>("manual");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }
    setMode("manual");
    setPassword("");
    setShowPassword(false);
    setSubmitting(false);
  }, [open, user?.id]);

  const handleModeChange = (nextMode: PasswordMode) => {
    setMode(nextMode);
    if (nextMode === "generate") {
      setPassword(generatePassword());
      setShowPassword(true);
    } else {
      setPassword("");
      setShowPassword(false);
    }
  };

  const handleCopy = async () => {
    if (!password) return;
    try {
      await navigator.clipboard.writeText(password);
    } catch {
      // Clipboard may be unavailable; ignore quietly
    }
  };

  const handleSubmit = async () => {
    if (!user?.email) {
      return;
    }

    const trimmed = password.trim();
    if (trimmed.length < 6) {
      await MySwal.fire({
        icon: "error",
        title: <p>Password too short</p>,
        text: "Password must be at least 6 characters and include upper and lowercase letters.",
      });
      return;
    }

    setSubmitting(true);
    try {
      const result = await UsersService.postApiUsersResetPasswordByAdmin({
        email: user.email,
        newPassword: trimmed,
      });

      if (!result.success) {
        await MySwal.fire({
          icon: "error",
          title: <p>Could not reset password</p>,
          text: result.message ?? "Please try again.",
        });
        return;
      }

      onClose();
      await MySwal.fire({
        icon: "success",
        title: <p>Password reset</p>,
        text: `A new password was set for ${user.email} and emailed to them.`,
      });
    } catch {
      await MySwal.fire({
        icon: "error",
        title: <p>Could not reset password</p>,
        text: "Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const fullName =
    `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim() || "this user";

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Reset password</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Set a new password for <strong>{fullName}</strong> ({user?.email}). The
          password will be emailed to them.
        </Typography>

        <FormControl component="fieldset" sx={{ mb: 2 }}>
          <RadioGroup
            row
            value={mode}
            onChange={(e) => handleModeChange(e.target.value as PasswordMode)}
          >
            <FormControlLabel
              value="manual"
              control={<Radio size="small" />}
              label="Enter password"
            />
            <FormControlLabel
              value="generate"
              control={<Radio size="small" />}
              label="Auto-generate"
            />
          </RadioGroup>
        </FormControl>

        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
          <TextField
            fullWidth
            required
            label="New password"
            variant="outlined"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            helperText="At least 6 characters, with upper and lowercase letters."
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword((show) => !show)}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {mode === "generate" && (
            <>
              <IconButton
                aria-label="regenerate password"
                onClick={() => setPassword(generatePassword())}
                title="Regenerate"
                sx={{ mt: 0.5 }}
              >
                <RefreshIcon />
              </IconButton>
              <IconButton
                aria-label="copy password"
                onClick={handleCopy}
                title="Copy"
                sx={{ mt: 0.5 }}
                disabled={!password}
              >
                <ContentCopyIcon />
              </IconButton>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !password.trim()}
        >
          {submitting ? "Resetting..." : "Reset & email"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
